import React, { useEffect, useMemo } from "react";
import { useParams, useLocation, Navigate, Link } from "react-router-dom";
import { getTemplate } from "../data/templates";
import {
  themeStyle,
  TemplateNav,
  TemplateFooter,
  Hero,
  renderModule,
  StatsStrip,
  WhyChooseUs,
  ModuleBanner,
  BottomCTA,
} from "../components/template/Sections";
import MobileAppPreview from "../components/template/MobileAppPreview";

// Routes:
//   /templates/:siteKey                    → Rich Home (hero → stats → about → catalog → why → gallery → people → app preview → reviews → CTA → footer)
//   /templates/:siteKey/:moduleSlug        → Module banner → module → bottom CTA → footer
//   /templates/:siteKey/contact            → Same as above (handled by react-router)
export default function TemplatePage() {
  const { siteKey, moduleSlug } = useParams();
  const location = useLocation();
  const template = getTemplate(siteKey);

  // Scroll to top on each route change for a real-site feel
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  const explicitSlug = moduleSlug || (location.pathname.endsWith("/contact") ? "contact" : null);
  const navItem = useMemo(
    () => (explicitSlug && template ? template.nav.find((n) => n.slug === explicitSlug) : null),
    [explicitSlug, template]
  );
  const moduleData =
    explicitSlug && navItem && template ? { ...template.modules[explicitSlug], type: navItem.type } : null;

  // Decide which modules to feature on the rich home page
  const homeSections = useMemo(() => {
    if (!template) return [];
    // Order of preference for richer rendering
    const order = [
      "about",
      "services",
      "products",
      "menu",
      "collections",
      "packages",
      "rooms",
      "properties",
      "academics",
      "facilities",
      "departments",
      "features",
      "doctors",
      "trainers",
      "gallery",
      "lookbook",
      "portfolio",
      "photo-view",
      "reviews",
      "testimonials",
      "pricing",
    ];
    const allowedSlugs = template.nav.map((n) => n.slug);
    return order.filter((s) => allowedSlugs.includes(s) && template.modules[s]);
  }, [template]);

  if (!template) {
    return <Navigate to="/services" replace />;
  }

  return (
    <div style={themeStyle(template.theme)} className="min-h-screen flex flex-col" data-testid={`template-${template.key}`}>
      <TemplateNav template={template} />

      <main className="flex-1">
        {!explicitSlug ? (
          // ─── RICH HOME ──────────────────────────────────────────────────
          <>
            <Hero template={template} hero={template.hero} />
            <StatsStrip template={template} />

            {/* About (if present) */}
            {template.modules.about &&
              renderModule({ ...template.modules.about, type: "about" }, "about", template)}

            {/* Primary catalog (services/products/packages/rooms/etc.) */}
            {(() => {
              const primary = homeSections.find((s) =>
                ["services", "products", "menu", "collections", "packages", "rooms", "properties", "academics", "departments", "features"].includes(s)
              );
              if (!primary) return null;
              const nav = template.nav.find((n) => n.slug === primary);
              return renderModule({ ...template.modules[primary], type: nav?.type }, primary, template);
            })()}

            {/* Why choose us */}
            <WhyChooseUs template={template} />

            {/* Secondary catalog if multiple exist */}
            {(() => {
              const catalogs = homeSections.filter((s) =>
                ["services", "products", "menu", "collections", "packages", "rooms", "properties", "academics", "facilities", "features"].includes(s)
              );
              if (catalogs.length < 2) return null;
              const second = catalogs[1];
              const nav = template.nav.find((n) => n.slug === second);
              return renderModule({ ...template.modules[second], type: nav?.type }, second, template);
            })()}

            {/* People preview (doctors / trainers) */}
            {(() => {
              const peopleSlug = ["doctors", "trainers"].find((s) => template.modules[s]);
              if (!peopleSlug) return null;
              const nav = template.nav.find((n) => n.slug === peopleSlug);
              return renderModule({ ...template.modules[peopleSlug], type: nav?.type }, peopleSlug, template);
            })()}

            {/* Gallery preview (if exists) — show only first module */}
            {(() => {
              const gallerySlug = ["gallery", "lookbook", "portfolio", "photo-view"].find((s) => template.modules[s]);
              if (!gallerySlug) return null;
              const data = template.modules[gallerySlug];
              const trimmed = { ...data, images: (data.images || []).slice(0, 8) };
              const nav = template.nav.find((n) => n.slug === gallerySlug);
              return renderModule({ ...trimmed, type: nav?.type }, gallerySlug, template);
            })()}

            {/* Mobile App preview — always shown */}
            <MobileAppPreview template={template} />

            {/* Reviews / Testimonials */}
            {(() => {
              const slug = ["reviews", "testimonials"].find((s) => template.modules[s]);
              if (!slug) return null;
              const nav = template.nav.find((n) => n.slug === slug);
              return renderModule({ ...template.modules[slug], type: nav?.type }, slug, template);
            })()}

            {/* Bottom CTA */}
            <BottomCTA template={template} />
          </>
        ) : !moduleData || !navItem ? (
          <Navigate to={`/templates/${template.key}`} replace />
        ) : (
          // ─── INNER MODULE PAGE ─────────────────────────────────────────
          <>
            <ModuleBanner template={template} navItem={navItem} module={moduleData} />
            {renderModule(moduleData, explicitSlug, template)}
            <BottomCTA template={template} />
          </>
        )}
      </main>

      <TemplateFooter template={template} />
    </div>
  );
}
